import MarketplaceApi from 'api/MarketplaceApi';
import { IAuctionBiderObj, IItemDetail, IPlaceItemRequestPayload } from 'api/types/marketplace';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import qs from 'query-string';
import { getCatchError } from 'utils/errorHandler';

import Loader from '../../assets/images/loader/spin.gif';
import Header from '../../layouts/header';
// import HomeBanner from '../../layouts/Banner';
import CardDetails from './partials/CardDetails';
import { Context } from 'contexts/ContextProvider';
import { approveToken, bidItem, buyItem, checkTokenAllowance, itemRecallRequest } from 'utils/interact';
import { ModalStatus } from 'config/constants';
import ConfirmationModal from './partials/ConfirmationModal';
import CustomToastComponent from 'layouts/CustomToastComponent';
import { ethers } from 'ethers';
import AppConfig from 'config/config';

function HomePage() {
  const navigate = useNavigate();
  const { id } = qs.parse(window.location.search);
  const [itemDetailObj, setItemDetailObj] = useState<IItemDetail | null>(null);
  const [auctionList, setAuctionList] = useState<Array<IAuctionBiderObj>>([]);
  const [itemDetailFetchLoader, setItemDetailFetchLoader] = useState<boolean>(true);
  const [itemRequestLoader, setItemRequestLoader] = useState<boolean>(false);
  const [itemRecallRequestLoader, setItemRecallRequestLoader] = useState<boolean>(false);
  const [amount, setAmount] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [modalStatus, setModalStatus] = useState<number>(ModalStatus.request); // 0 stable, 1 error, 2 success
  const [modalErrorMessage, setModalErrorMessage] = useState<string>(''); // 0 stable, 1 error, 2 success
  const [transaction, setTransaction] = useState<any>(null);
  const [requestModalOpen, setRequestModalOpen] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [quantityBid, setQuantityBid] = useState<string>('');

  const [approveTokenLoader, setApproveTokenLoader] = useState<boolean>(false);
  const [paperTokenAllowance, setPaperTokenAllowance] = useState<string>('0');
  const [isTokenApproved, setIsTokenApproved] = useState<boolean>(false);

  const quantityChangeHandler = (value: string, maxQuantity: number) => {
    let updateValue = `0`;
    if (value && parseInt(value)) {
      updateValue = parseInt(value) < maxQuantity ? `${parseInt(value)}` : `${maxQuantity}`;
    }

    setQuantityBid(updateValue);
  };

  const { walletAddress, web3Provider, fetchTokenBalance } = useContext(Context);

  const checkTokenApproval = useCallback(async () => {
    if (!walletAddress || !web3Provider) {
      return;
    }

    setApproveTokenLoader(true);
    try {
      const response = await checkTokenAllowance(walletAddress, web3Provider);
      if (!response.error) {
        if (ethers.BigNumber.from(response.allowance.toString()).gt(ethers.BigNumber.from('0'))) {
          setPaperTokenAllowance(response.allowance.toString());
          setIsTokenApproved(true);
        }
      }
    } catch (error: any) {
      toast.error(<CustomToastComponent message={error.message} />);
    }
    setApproveTokenLoader(false);
  }, [walletAddress, web3Provider]);

  const approveTokenHandler = async () => {
    setApproveTokenLoader(true);
    setModalStatus(ModalStatus.request);
    setRequestModalOpen(!requestModalOpen);

    const response = await approveToken(web3Provider);

    if (response.error) {
      setModalStatus(ModalStatus.error);
      setModalErrorMessage(response.errorMessage);
      setApproveTokenLoader(false);
      return;
    }

    // setIsTokenApproved(true);
    setApproveTokenLoader(false);
    setTransaction(response.tx);
    setModalStatus(ModalStatus.success);

    // const receipt = await response.tx.wait();
    await response.tx.wait();

    // fetching the token allowance balance again after approval
    await checkTokenApproval();

    if (ethers.BigNumber.from(paperTokenAllowance).gt(ethers.BigNumber.from('0'))) {
      toast.success(
        <CustomToastComponent
          message="$PAPER token spend approved successfully"
          link={`${AppConfig.EXPLORER_URL}/tx/${response.tx.hash}`}
        />,
      );
    }
  };

  const itemRecallRequestHandler = async () => {
    try {
      if (!walletAddress || !web3Provider || !itemDetailObj) {
        return;
      }

      setItemRecallRequestLoader(true);
      setModalStatus(ModalStatus.request);
      setRequestModalOpen(!requestModalOpen);

      const itemReqRes = await MarketplaceApi.itemRecallRequest(walletAddress);

      const response: any = await itemRecallRequest(
        web3Provider,
        itemReqRes.data.data.signature,
        itemReqRes.data.data.itemIds,
        itemReqRes.data.data.quantity,
      );
      if (response.error) {
        setModalStatus(ModalStatus.error);
        setModalErrorMessage(response.errorMessage);
        setItemRecallRequestLoader(false);
        return;
      }

      const transactionHash = response.tx.hash;
      setTransaction({
        hash: transactionHash,
      });

      setModalStatus(ModalStatus.success);

      // const receipt = await response.tx.wait();
      await response.tx.wait();

      // Logging in DB
      // await MarketplaceApi.reclaimRequest(walletAddress, transactionHash);
      // setRefreshData(true);

      setItemDetailObj((prevState: IItemDetail | null) => {
        if (prevState) {
          return { ...prevState, recallItemIds: [], recallBidAmount: 0 };
        } else {
          return null;
        }
      });

      toast.success(<CustomToastComponent message="Bid Reclaimed successfully" />);
    } catch (err) {
      setModalStatus(ModalStatus.error);
      setModalErrorMessage(getCatchError(err)[0]);
    } finally {
      setItemRecallRequestLoader(false);
    }
  };

  // BUY and RAFFLE and BID action handlers
  const itemRequestHandler = async () => {
    try {
      if (!walletAddress || !web3Provider || !itemDetailObj) {
        return;
      }

      const postData: IPlaceItemRequestPayload = {
        requestType: itemDetailObj.sellType,
        senderAddress: walletAddress,
      };

      if (itemDetailObj.sellType === 'bid' && itemDetailObj.bidStartPaper) {
        if (!parseInt(amount) || parseInt(amount) <= 0) {
          toast.error('Please enter a valid bid amount');
          return;
        }

        if (parseInt(amount) < parseInt(itemDetailObj.bidStartPaper)) {
          toast.error('Please enter bid amount greater than or equal to min bid amount');
          return;
        }

        postData.amount = amount;
      }

      if (itemDetailObj.sellType === 'ranked_bid' && itemDetailObj.bidStartPaper) {
        if (itemDetailObj.allowedQuantityUpdate && (!parseInt(quantityBid) || parseInt(quantityBid) <= 0)) {
          toast.error('Please enter a valid bid quantity');
          return;
        }

        if (parseInt(amount) < parseInt(itemDetailObj.bidStartPaper)) {
          toast.error('Please enter bid amount greater than or equal to min bid amount');
          return;
        }

        postData.quantity = quantityBid;
        postData.amount = amount;
      }

      if (itemDetailObj.sellType === 'raffle') {
        if (!parseInt(quantity) || parseInt(quantity) <= 0) {
          toast.error('Please enter a valid quantity');
          return;
        }

        postData.quantity = quantity;
      }

      setItemRequestLoader(true);

      const placeItemReqRes = await MarketplaceApi.placeItemRequest(itemDetailObj.id, postData);

      let paperTokenAllowanceCheckAmount = 0;
      if (itemDetailObj.sellType === 'bid' || itemDetailObj.sellType === 'ranked_bid') {
        paperTokenAllowanceCheckAmount =
          parseInt(placeItemReqRes.data.data.bidAmount) * placeItemReqRes.data.data.quantity;
      } else {
        paperTokenAllowanceCheckAmount =
          parseInt(placeItemReqRes.data.data.buyPricePaper) * placeItemReqRes.data.data.quantity;
      }

      if (
        paperTokenAllowanceCheckAmount > 0 &&
        !ethers.BigNumber.from(paperTokenAllowance).gte(
          ethers.BigNumber.from(paperTokenAllowanceCheckAmount.toString()),
        )
      ) {
        toast.error('Not enough $PAPER token spend allowance. Please approve token to proceed.');
        setIsTokenApproved(false);
        setItemRequestLoader(false);
        return;
      }

      setModalStatus(ModalStatus.request);
      setRequestModalOpen(!requestModalOpen);

      const response: any =
        itemDetailObj.sellType === 'ranked_bid' || itemDetailObj.sellType === 'bid'
          ? await bidItem(
              web3Provider,
              itemDetailObj.id,
              placeItemReqRes.data.data.signature,
              placeItemReqRes.data.data.bidAmount.toString(),
              placeItemReqRes.data.data.quantity,
            )
          : await buyItem(
              web3Provider,
              itemDetailObj.id,
              placeItemReqRes.data.data.signature,
              placeItemReqRes.data.data.quantity,
              placeItemReqRes.data.data.buyPricePaper ? placeItemReqRes.data.data.buyPricePaper : '0',
              placeItemReqRes.data.data.buyPriceEth ? placeItemReqRes.data.data.buyPriceEth : '0',
              placeItemReqRes.data.data.maxAllowedQuantity,
            );
      if (response.error) {
        setModalStatus(ModalStatus.error);
        setModalErrorMessage(response.errorMessage);
        setItemRequestLoader(false);
        return;
      }

      const transactionHash = response.tx.hash;
      setTransaction({
        hash: transactionHash,
      });

      setModalStatus(ModalStatus.success);

      // const receipt = await response.tx.wait();
      await response.tx.wait();

      if (itemDetailObj.sellType === 'buy' || itemDetailObj.sellType === 'raffle') {
        toast.success(<CustomToastComponent message="Item bought successfully" />);
        // await MarketplaceApi.itemBoughtRequest(itemDetailObj.id, walletAddress, transactionHash);
      } else {
        toast.success(<CustomToastComponent message="Bid placed successfully" />);
        // await MarketplaceApi.itemBidRequest(itemDetailObj.id, walletAddress, transactionHash);
      }
      setRefreshData(true);
      fetchTokenBalance();
    } catch (err) {
      setModalStatus(ModalStatus.error);
      setModalErrorMessage(getCatchError(err)[0]);
    } finally {
      setItemRequestLoader(false);
    }
  };

  const fetchItemDetail = useCallback(async () => {
    try {
      if (!parseInt(id as string)) {
        toast.error('Invalid ID');
        navigate(`/`);
        return;
      }

      setItemDetailFetchLoader(true);

      const response = await MarketplaceApi.getItemDetail(parseInt(id as string), walletAddress);

      setItemDetailObj(response.data.data.itemDetail);
      setAuctionList(response.data.data.auctionList ?? []);

      if (walletAddress && response.data.data.itemDetail.sellType === 'ranked_bid') {
        if (response.data.data.itemDetail.maxQuantity <= 0) {
          toast.error('You are not allowed to place bid for this item');
        }

        if (response.data.data.itemDetail.currentBidQuantity > 0) {
          setQuantityBid(response.data.data.itemDetail.currentBidQuantity.toString());
        }
      }
    } catch (err) {
      toast.error(getCatchError(err)[0]);
      navigate(`/`);
    } finally {
      setItemDetailFetchLoader(false);
    }
  }, [id, walletAddress, navigate]);

  useEffect(() => {
    if (id) {
      fetchItemDetail();
    }
  }, [id, walletAddress, fetchItemDetail]);

  useEffect(() => {
    if (walletAddress) {
      checkTokenApproval();
    }
  }, [walletAddress, checkTokenApproval]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);
      fetchItemDetail();
    }
  }, [refreshData, fetchItemDetail]);

  return (
    <div className="app-wrapper">
      <Header />
      <main className="main-wrapper">
        {/* <HomeBanner /> */}

        {itemDetailFetchLoader && !refreshData && (
          <div className="section-loader">
            <div className="loader">
              <img src={Loader} className="img-fluid" alt="Loader" />
            </div>
          </div>
        )}

        {(!itemDetailFetchLoader || refreshData) && itemDetailObj && (
          <Fragment>
            <CardDetails
              itemDetailObj={itemDetailObj}
              auctionList={auctionList}
              itemRequestHandler={itemRequestHandler}
              itemRequestLoader={itemRequestLoader || refreshData}
              setAmount={setAmount}
              amount={amount}
              setQuantity={setQuantity}
              quantity={quantity}
              itemRecallRequestLoader={itemRecallRequestLoader}
              itemRecallRequestHandler={itemRecallRequestHandler}
              approveTokenLoader={approveTokenLoader}
              isTokenApproved={isTokenApproved}
              approveTokenHandler={approveTokenHandler}
              quantityChangeHandler={quantityChangeHandler}
              quantityBidValue={quantityBid}
            />
          </Fragment>
        )}

        <ConfirmationModal
          modalOpen={requestModalOpen}
          modalToggler={setRequestModalOpen}
          modalStatus={modalStatus}
          modalErrorMessage={modalErrorMessage}
          transaction={transaction}
        />
      </main>
    </div>
  );
}

export default HomePage;
